/**
 * A cache implementation with time-based expiration for key-value pairs.
 * @template Key - The type of the cache key.
 * @template Value - The type of the cache value.
 */
export class TimeCache {
    constructor(ttlMs, autoClean = false) {
        this.ttlMs = ttlMs;
        this.autoClean = autoClean;
        this.cache = new Map();
    }
    /**
     * Set a key-value pair in the cache.
     *
     * @param {Key} key - The key of the pair to be set.
     * @param {Value} value - The value of the pair to be set.
     */
    set(key, value) {
        this.cache.set(key, {
            value,
            timestamp: Date.now(),
        });
        this.enabledAutoClean();
    }
    /**
     * Checks if the cache has a record for the given key and if it is still within the time-to-live (TTL) period.
     * @param {Key} key - The key to check the cache for.
     * @return {boolean} - Returns true if the cache has a record for the key within the TTL period, otherwise false.
     */
    get(key) {
        const record = this.cache.get(key);
        if (!record)
            return null;
        if (Date.now() - record.timestamp > this.ttlMs) {
            this.cache.delete(key);
            return null;
        }
        return record.value;
    }
    /**
     * Checks if the cache has a record for the given key and if it is still within the time-to-live (TTL) period.
     * @param {Key} key - The key to check the cache for.
     * @return {boolean} - Returns true if the cache has a record for the key within the TTL period, otherwise false.
     */
    has(key) {
        const record = this.cache.get(key);
        if (!record)
            return false;
        if (Date.now() - record.timestamp > this.ttlMs) {
            this.cache.delete(key);
            return false;
        }
        return true;
    }
    delete(key) {
        return this.cache.delete(key);
    }
    clear() {
        this.cache.clear();
        if (!this.autoCleanTimer)
            return;
        clearInterval(this.autoCleanTimer);
        this.autoCleanTimer = undefined;
    }
    size() {
        this.collectGarbage();
        return this.cache.size;
    }
    enabledAutoClean() {
        if (!this.autoClean || this.autoCleanTimer)
            return;
        this.autoCleanTimer = setInterval(() => {
            this.collectGarbage();
        }, this.ttlMs);
    }
    collectGarbage() {
        for (const key of [...this.cache.keys()]) {
            this.get(key);
        }
    }
}
//# sourceMappingURL=time-cache.js.map